import * as React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _omit from 'lodash/omit';
import {
  Radio,
  Input,
  Upload,
  DatePicker,
  Editor,
  InputNumber,
  Select,
  TreeSelect,
} from '@beehive/components';
import { showAlert, prettyBytes, beforeUploadSuccess } from 'utils';
import { DEFAULT_DATE_FORMAT } from 'utils/date';
import messages from 'messages';
import { MockInputShow } from 'containers/OutBidding/components/utils';
const MAX_SIZE_LIMIT = 1024 * 1024 * 200;

export type ChangeEvent = React.ChangeEvent<HTMLInputElement>;

export interface PureFormItemProps {
  componentClass?: string | React.ComponentClass<any> | React.FC;
  className?: string;
  formItemKey: string;
  value?: any;
  options?: any[];
  multiple?: boolean;
  maxSizeLimit?: number;
  minSizeLimit?: number;
  onChange?: Function;
  uploadAction?: string;
  placeholder?: string;
  isShow?: boolean;
  showComponent?: React.ReactElement;
}
export interface ChangeParams {
  formItemKey: string;
  value: any;
}

export default class PureFormItem extends React.PureComponent<
  PureFormItemProps,
  any
> {
  static contextTypes = {
    intl: PropTypes.object,
  };

  private componentRef: any;

  static defaultProps = {
    maxSizeLimit: MAX_SIZE_LIMIT,
  };

  handleChange = (value: any, key?: any) => {
    const { onChange, formItemKey } = this.props;
    console.log("🚀 ~ PureFormItem ~ onChange:", onChange)
    onChange &&
      onChange({
        formItemKey: typeof key === 'string' ? key : formItemKey,
        value,
      });
  }

  handleBeforeUpload = (file: any, fileList: any) => {
    const { maxSizeLimit = MAX_SIZE_LIMIT, minSizeLimit } = this.props;
    const { intl } = this.context;
    if (file.size && file.size > maxSizeLimit) {
      showAlert({
        type: 'error',
        message: intl.formatMessage(messages.fileExceededLimit, {
          filename: file.name,
          fileMaxSize: prettyBytes(maxSizeLimit),
        }),
      });
      return false;
    }
    if (typeof minSizeLimit === 'number' && minSizeLimit >= 0) {
      let msg = '';
      if (minSizeLimit === 0 && file.size === 0) {
        msg = '不能上传空文件!';
      }
      if (file.size < minSizeLimit) {
        msg = `文件过小,${file.name}大小低于${prettyBytes(minSizeLimit)}！`;
      }
      if (msg) {
        showAlert({
          type: 'error',
          message: msg,
        });
        return false;
      }
    }
    return true;
  }

  saveComponentRef = (el: any) => {
    this.componentRef = el;
  }
  /**
   * getValue
   */
  public getValue() {
    if (this.componentRef && this.componentRef.getValue) {
      return this.componentRef.getValue();
    }
    return true;
  }

  renderItem = () => {
    const { intl } = this.context;
    const {
      formItemKey,
      componentClass,
      options,
      value,
      multiple,
      onChange,
      children,
      isShow = false,
      showComponent,
      ...others
    } = this.props;

    // 查看模式
    if (isShow) {
      if (showComponent) {
        return showComponent;
      } else {
        return <MockInputShow text={value}/>;
      }
    }

    const otherProps = _omit(others, ['maxSizeLimit']);

    Object.assign(otherProps, { ref: this.saveComponentRef });

    switch (componentClass) {
      case 'radio': {
        return (
          <Radio.Group
            onChange={(evt: ChangeEvent) => {
              const v = evt.target.value;
              this.handleChange(v);
            }}
            value={value}
            items={options}
            {...otherProps}
          />
        );
      }
      case 'select': {
        return (
          <Select
            isMultiple={multiple}
            options={options}
            value={value}
            mode="select"
            onChange={(v: any) => this.handleChange(v)}
            {...otherProps}
          />
        );
      }
      case 'treeSelect': {
        return (
          <TreeSelect
            isMultiple={multiple}
            options={options}
            value={value}
            mode="select"
            onChange={(v: any) => this.handleChange(v)}
            {...otherProps}
          />
        );
      }
      case 'textarea': {
        return (
          <Input.TextArea
            autosize
            value={value}
            onChange={(evt: ChangeEvent) => this.handleChange(evt.target.value)}
            {...otherProps}
          />
        );
      }
      case 'input':
        return (
          <Input
            value={value}
            onChange={(evt: ChangeEvent) => this.handleChange(evt.target.value)}
            {...otherProps}
          />
        );
      case 'inputNumber': {
        return (
          <InputNumber
            placeholder={intl.formatMessage(messages.inputPlaceholder)}
            precision={2}
            hideHandle
            min={0}
            value={value}
            onChange={(v: number) => this.handleChange(v)}
            {...otherProps}
          />
        );
      }
      case 'datePicker': {
        const finalValue = moment.isMoment(value)
          ? value
          : value && moment(value).isValid()
          ? moment(value)
          : value;
        return (
          <DatePicker
            size="large"
            value={finalValue}
            onChange={(v: any) => {
              let finalV = moment(v);
              finalV = v && finalV.isValid() ? finalV.format(DEFAULT_DATE_FORMAT) : v;
              return this.handleChange(finalV);
            }}
            {...otherProps}
          />
        );
      }
      case 'editor': {
        return (
          <Editor
            value={value}
            onChange={(v: any) => this.handleChange(v)}
            {...otherProps}
          />
        );
      }
      case 'upload': {
        const { uploadAction = '/api/v1/attachments/upload' } = this.props;
        return (
          <Upload
            multiple
            action={uploadAction}
            showUploadList={{ showRemoveIcon: true }}
            onChange={(options: any) => this.handleChange(options)}
            beforeUpload={this.handleBeforeUpload}
            beforeSuccess={beforeUploadSuccess}
            {...otherProps}
          />
        );
      }
      default: {
        if (componentClass) {
          return React.createElement(
            componentClass,
            {
              ...this.props,
              ref: this.saveComponentRef,
              onChange: this.handleChange,
            },
            children
          );
        } else {
          return <span className={otherProps.className}>{value}</span>;
        }
      }
    }
  }
  public render() {
    return this.renderItem();
  }
}
